import { afterNextRender, Component, computed, inject, input, signal } from '@angular/core';
import { form, FormField, FormRoot, hidden, pattern, required } from '@angular/forms/signals';
import { Router, RouterLink } from '@angular/router';
import { RetryUtils } from '@unimarket/shared';

import { Checkout, PaymentMethod } from '../../model/checkout';
import { StorefrontPage } from '../../model/storefront-page';
import { WayforpayResponse } from '../../model/wayforpay';
import { AnalyticsService } from '../../service/analytics.service';
import { CartService } from '../../service/cart.service';
import { PageFailure } from '../../service/page-failure';
import { SeoService } from '../../service/seo.service';
import { PaymentClosed, WayforpayService } from '../../service/wayforpay.service';
import { WebsiteService } from '../../service/website.service';
import { MoneyPipe } from '../../money.pipe';

const BRANCH = 'Відділення Нової пошти';
const LOCKER = 'Поштомат Нової пошти';
const COURIER = 'Доставка кур\'єром за адресою';
const PICKUP = 'Самовивіз з магазину у м. Львів';

const ADDRESS_LABELS: Record<string, string> = {
  [BRANCH]: 'Адреса або номер відділення:',
  [LOCKER]: 'Номер поштомату:',
  [COURIER]: 'Адреса доставки кур\'єром:',
  [PICKUP]: 'Адреса магазину самовивозу (необов\'язково):'
};
const PHONE_PATTERN = /^([0-9]{10}|38[0-9]{10}|\+38[0-9]{10})$/;
const NOT_BLANK = /\S/;

/** How many times the order is offered to the API before the visitor is told it did not work. */
const ATTEMPTS = 3;

/** A 4xx is the API's considered answer - a sold out line, an empty cart - and will not change. */
function worthRetrying(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === undefined || status < 400 || status >= 500;
}

/** The API's own words, translated, out of `ApiExceptionConfig.ErrorResponse`. */
function reason(error: unknown, otherwise = 'Не вдалося створити замовлення. Спробуйте ще раз.'): string {
  const body = (error as { error?: { message?: string } } | null)?.error;
  return body?.message || otherwise;
}

/** The `checkout` template with its `order-parameters` partial: the shipping form and the basket. */
@Component({
  selector: 'checkout-page',
  imports: [FormField, FormRoot, RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  styles: `
    .bo4.invalid {
      border-color: #dc3545;
    }
    .field-error {
      width: 100%;
      margin: -6px 0 12px;
      font-size: 13px;
      color: #dc3545;
    }
  `,
  template: `
    <!-- Title Page -->
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h1 class="l-text2 t-center">
        Замовлення
      </h1>
    </section>

    <!-- Cart -->
    <section class="cart bgwhite p-t-7 p-b-70">
      <div class="container">
        @if (cart().products.length) {
          <div class="row">
            <div class="col-lg">
              <!-- Total -->
              <div class="bo9 p-l-40 p-r-40 p-t-30 p-b-38 m-t-30 m-r-auto m-l-auto p-lr-15-sm">
                <h5 class="m-text20 p-b-24">
                  Замовлення
                </h5>

                <form [formRoot]="checkoutForm">
                  <div class="flex-w flex-sb-m p-b-12 subtotal">
                    <span class="s-text18 w-size19 w-full-sm">
                      Сума:
                    </span>

                    <span class="m-text21 w-size20 w-full-sm">
                      {{cart().total | money}}
                    </span>
                  </div>
                  <div class="flex-w flex-sb-m p-b-12 subtotal">
                    <span class="s-text18 w-size19 w-full-sm">
                      Вартість доставки:
                    </span>

                    <span class="s-text8 w-size20 w-full-sm">
                      згідно стандартних тарифів
                    </span>
                  </div>

                  <div class="flex-w flex-sb bo10 p-t-15 p-b-20 shipping">
                    <span class="s-text18 w-size19 w-full-sm">
                      Доставка і оплата:
                    </span>

                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">
                        Наш магазин доставляє товари через компанію Нова пошта
                        (у відділення, поштомат або адресна доставка кур'єром).
                        <br>
                        Стандартною формою оплати є накладений платіж, також можлива оплата
                        @if (data().wayforpay) {карткою або }банківським переказом на рахунок
                        компанії за реквізитами (IBAN).
                      </p>
                    </div>

                    <span class="s-text19 sizefull text-center">
                      Параметри замовлення
                    </span>
                    <span class="s-text18 w-full-sm m-t-8">
                      Варіант доставки:
                    </span>
                    <div class="bo4 of-hidden w-full-sm m-b-12 sizefull">
                      <select class="select-native" id="shippingCompany"
                        [formField]="checkoutForm.shippingCompany">
                        @for (option of methods; track option) {
                          <option>{{option}}</option>
                        }
                      </select>
                    </div>
                    <span class="s-text18 w-full-sm">
                      Ім’я та прізвище одержувача:
                    </span>
                    <div class="row sizefull m-0">
                      <div class="col p-0 pr-1">
                        @let firstName = checkoutForm.address.firstName();
                        <div class="size15 bo4 m-b-12" [class.invalid]="firstName.touched() && firstName.invalid()">
                          <input class="sizefull s-text7 p-l-15 p-r-15" type="text" placeholder="Ім'я"
                            id="firstName" autocomplete="given-name"
                            aria-describedby="firstNameError"
                            [formField]="checkoutForm.address.firstName"
                            [attr.aria-invalid]="firstName.touched() && firstName.invalid()">
                        </div>
                        @if (firstName.touched() && firstName.invalid()) {
                          <p class="field-error" id="firstNameError">Вкажіть ім’я</p>
                        }
                      </div>
                      <div class="col p-0 pl-1">
                        @let lastName = checkoutForm.address.lastName();
                        <div class="size15 bo4 m-b-12" [class.invalid]="lastName.touched() && lastName.invalid()">
                          <input class="sizefull s-text7 p-l-15 p-r-15" type="text" placeholder="Прізвище"
                            id="lastName" autocomplete="family-name"
                            aria-describedby="lastNameError"
                            [formField]="checkoutForm.address.lastName"
                            [attr.aria-invalid]="lastName.touched() && lastName.invalid()">
                        </div>
                        @if (lastName.touched() && lastName.invalid()) {
                          <p class="field-error" id="lastNameError">Вкажіть прізвище</p>
                        }
                      </div>
                    </div>
                    <span class="s-text18 w-full-sm">
                      Телефон:
                    </span>
                    @let phone = checkoutForm.address.phone();
                    <div class="size15 bo4 m-b-12" [class.invalid]="phone.touched() && phone.invalid()">
                      <input value="+38" readonly
                        class="p-l-1 p-r-4 border border-left-0 border-top-0 border-bottom-0 s-text7 font-weight-bold
                          w-15 h-99 text-right"/>
                      <input class="s-text7 w-80 h-99" type="tel" id="phone" autocomplete="tel-national"
                        placeholder="Номер у форматі 0981234567"
                        aria-describedby="phoneError"
                        [formField]="checkoutForm.address.phone"
                        [attr.aria-invalid]="phone.touched() && phone.invalid()">
                    </div>
                    @if (phone.touched() && phone.invalid()) {
                      <p class="field-error" id="phoneError">
                        @if (phone.errors()[0].kind === 'required') {
                          Вкажіть номер телефону
                        } @else {
                          Введіть мобільний номер у форматі 0931234567
                        }
                      </p>
                    }
                    @if (!checkoutForm.address.city().hidden()) {
                      <span class="s-text18 w-full-sm">
                        Місто:
                      </span>
                      @let city = checkoutForm.address.city();
                      <div class="size15 bo4 m-b-12" [class.invalid]="city.touched() && city.invalid()">
                        <input class="sizefull s-text7 p-l-15 p-r-15" type="text" id="city"
                          placeholder="Місто доставки" autocomplete="address-level2"
                          aria-describedby="cityError"
                          [formField]="checkoutForm.address.city"
                          [attr.aria-invalid]="city.touched() && city.invalid()">
                      </div>
                      @if (city.touched() && city.invalid()) {
                        <p class="field-error" id="cityError">Вкажіть місто</p>
                      }
                    }
                    <span class="s-text18 w-full-sm">
                      {{addressLabel()}}
                    </span>
                    @let street = checkoutForm.address.street();
                    <div class="size15 bo4 m-b-12" [class.invalid]="street.touched() && street.invalid()">
                      <input class="sizefull s-text7 p-l-15 p-r-15" type="text" id="street"
                        autocomplete="street-address"
                        aria-describedby="streetError"
                        [formField]="checkoutForm.address.street"
                        [attr.aria-invalid]="street.touched() && street.invalid()">
                    </div>
                    @if (street.touched() && street.invalid()) {
                      <p class="field-error" id="streetError">Вкажіть, куди доставити замовлення</p>
                    }
                    @if (website().bankDetails || data().wayforpay) {
                      <span class="s-text18 w-full-sm">
                        Оплата:
                      </span>
                      <div class="bo4 of-hidden w-full-sm m-b-12 sizefull">
                        <select class="select-native" id="paymentOption"
                          [formField]="checkoutForm.paymentMethod">
                          <option value="CASH_ON_DELIVERY">Накладений платіж</option>
                          @if (website().bankDetails) {
                            <option value="BANK_TRANSFER">Банківський переказ за реквізитами (IBAN)</option>
                          }
                          @if (data().wayforpay) {
                            <option value="CARD">Онлайн-оплата карткою</option>
                          }
                        </select>
                      </div>
                      @if (payment() === 'BANK_TRANSFER') {
                        <p class="s-text7 sizefull p-b-23 paymentOption" id="bankDetails">
                          <b>Банківські реквізити:</b>
                          <br>
                          <span [innerHTML]="website().bankDetails"></span>
                        </p>
                      }
                    }
                    <span class="s-text18 w-full-sm">
                      Коментар (необов’язково):
                    </span>
                    <div class="sizefull bo4">
                      <textarea class="dis-block s-text7 size20 bo4 p-l-22 p-r-22 p-t-13 m-b-20" id="comment"
                        placeholder="Коментар до замовлення" [formField]="checkoutForm.comment"></textarea>
                    </div>
                  </div>

                  @if (error()) {
                    <p class="s-text5 text-danger sizefull text-center" id="wayforpayError">
                      {{error()}}
                    </p>
                  }

                  <div class="size15 trans-0-4 paymentOption" id="submitOrder">
                    <button type="submit" class="flex-c-m sizefull bg1 bo-rad-23 m-t-20 hov1 s-text1 trans-0-4"
                      [disabled]="busy() || processing()">
                      {{payment() === 'CARD' ? 'Оплатити замовлення' : 'Підтвердити замовлення'}}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div class="col-lg">
              <div class="bo9 p-l-40 p-r-30 p-t-30 p-b-8 m-t-30 m-r-0 m-l-auto p-lr-15-sm">
                <h5 class="m-text20 p-b-24">
                  Товари
                </h5>

                <ul class="header-cart-wrapitem p-t-5">
                  @for (product of cart().products; track product.productId) {
                    <li class="header-cart-item">
                      <div class="header-cart-item-img">
                        <a [routerLink]="product.uri">
                          @if (product.photo) {
                            <img [src]="product.photo" [alt]="product.name">
                          }
                        </a>
                      </div>

                      <div class="header-cart-item-txt">
                        <a class="header-cart-item-name" [routerLink]="product.uri">
                          {{product.name}}
                        </a>

                        <span class="header-cart-item-info">
                          {{product.quantity}} x {{product.price | money}}
                        </span>
                      </div>
                    </li>
                  }
                </ul>

                <div class="header-cart-total">
                  Загалом: {{cart().total | money}}
                </div>
                <div class="row float-right">
                  <p class="p-t-28 p-b-28">
                    <a routerLink="/cart">&lt; Повернутись до кошика</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- The API may have emptied it while refusing the order; the visitor needs to know why. -->
          @if (error()) {
            <p class="s-text5 text-danger text-center p-b-20">{{error()}}</p>
          }
          <p class="text-center">
            Ваш кошик пустий. <a class="fglight" routerLink="/products">Додати товари?</a>
          </p>
        }
      </div>
    </section>
    `
})
export class CheckoutPageComponent {

  readonly data = input.required<StorefrontPage>();
  private cartService = inject(CartService);
  private router = inject(Router);
  private pageFailure = inject(PageFailure);
  private wayforpay = inject(WayforpayService);
  private analytics = inject(AnalyticsService);

  readonly methods = [BRANCH, LOCKER, COURIER, PICKUP];
  private readonly checkout = signal<Checkout & { comment: string }>({
    uuid: crypto.randomUUID(),
    shippingCompany: BRANCH,
    paymentMethod: 'CASH_ON_DELIVERY',
    comment: '',
    address: { firstName: '', lastName: '', phone: '', city: '', street: '' }
  });
  readonly method = computed(() => this.checkout().shippingCompany);
  readonly collected = computed(() => this.method() === PICKUP);
  readonly addressLabel = computed(() => ADDRESS_LABELS[this.method()]);
  readonly payment = computed<PaymentMethod>(() => this.checkout().paymentMethod);
  /**
   * Nothing is asked of a visitor who collects the order themselves except a name and a phone, so
   * the city is hidden - and a hidden field is not validated - and the address is optional.
   */
  readonly checkoutForm = form(this.checkout, path => {
    const { firstName, lastName, phone, city, street } = path.address;
    required(firstName);
    pattern(firstName, NOT_BLANK);
    required(lastName);
    pattern(lastName, NOT_BLANK);
    required(phone);
    pattern(phone, PHONE_PATTERN);
    hidden(city, { when: ({ valueOf }) => valueOf(path.shippingCompany) === PICKUP });
    required(city);
    pattern(city, NOT_BLANK);
    required(street, {
      when: ({ valueOf }) => valueOf(path.shippingCompany) !== PICKUP
    });
    pattern(street, NOT_BLANK, {
      when: ({ valueOf }) => valueOf(path.shippingCompany) !== PICKUP
    });
  }, {
    submission: {
      action: () => this.placeOrder(),
      onInvalid: root => root().errorSummary()[0]?.fieldTree().focusBoundControl()
    }
  });
  readonly cart = this.cartService.cart;
  readonly error = signal('');
  readonly busy = signal(false);
  /**
   * The order is in, though neither its page nor the API has been able to say so. Another order
   * now would be a second one, or a second charge to the card, so the form stays shut.
   */
  readonly processing = signal(false);
  readonly code = signal('');
  readonly website = inject(WebsiteService).current;

  constructor() {
    inject(SeoService).page(this.data, true);
    afterNextRender(() => {
      const { products, total } = this.cart();
      if (products.length) {
        this.analytics.event('begin_checkout', {
          currency: total.currency,
          value: total.value,
          items: products.map((product, index) => ({
            item_id: product.productId,
            item_name: product.name,
            index,
            price: product.price.value,
            quantity: product.quantity
          }))
        });
      }
    });
  }

  /** A city typed before choosing to collect the order is not one to send along. */
  private order(): Checkout {
    const checkout = this.checkout();
    return this.collected() ? { ...checkout, address: { ...checkout.address, city: '' } } : checkout;
  }

  /**
   * Written down first, paid for second. The card is charged by a widget in this browser, and a
   * browser can be closed, crash or lose its connection at any point - so the order goes to the
   * API before any money moves, and the payment is added to it afterwards. The worst this can
   * now leave behind is an order the shop can see and chase, never a charge with nothing behind it.
   *
   * Every press places the order again, from the form as it is now: the API replaces the one
   * still waiting for a card, and the cart stays until a card is approved, so a visitor whose
   * card was refused can change the address, or pay in cash, and simply press again.
   */
  private async placeOrder() {
    if (this.busy() || this.processing()) {
      return;
    }
    this.error.set('');
    this.code.set('');
    this.busy.set(true);
    try {
      const redirect = await this.place();
      if (redirect === undefined) {
        return;
      }
      if (this.code()) {
        await this.pay();
      } else {
        await this.leave(redirect);
      }
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * The order, offered more than once, because one lost to a dropped connection is one the shop
   * never hears about. Answers with where to go next, or nothing when it could not be placed;
   * an order waiting for a card has nowhere to go yet and answers with an empty string.
   */
  private async place(): Promise<string | undefined> {
    try {
      const placed = await RetryUtils.async(
        () => this.cartService.placeOrder(this.order()), ATTEMPTS, worthRetrying);
      if (placed.orderCode) {
        this.checkout.update(checkout => ({ ...checkout, uuid: crypto.randomUUID() }));
      }
      if (!placed.redirect) {
        this.code.set(placed.orderCode ?? '');
      }
      return placed.redirect ?? '';
    } catch (error) {
      if (!worthRetrying(error)) {
        await this.cartService.init();
      }
      this.error.set(reason(error));
      return undefined;
    }
  }

  /**
   * The card, and then the API checking it with Wayforpay. A refusal leaves the cart as it was,
   * for another try or another way to pay.
   */
  private async pay(): Promise<void> {
    let payment: WayforpayResponse;
    try {
      payment = await this.wayforpay.pay(this.code(), this.order().address);
    } catch (error) {
      this.error.set(this.retry(
        error instanceof PaymentClosed ? 'Оплату не завершено.' : reason(error, 'Оплата не пройшла.')));
      return;
    }
    try {
      await RetryUtils.async(() => this.cartService.confirmPayment(payment), ATTEMPTS, worthRetrying);
      await this.leave('/placeorder?orderCode=' + encodeURIComponent(this.code()));
    } catch (error) {
      if (!worthRetrying(error)) {
        // The API's considered answer, such as Wayforpay not having approved the card.
        this.error.set(this.retry(reason(error)));
        return;
      }
      // Wayforpay reports the payment to the API itself, so the order is paid for all the same.
      this.processing.set(true);
      this.error.set('Оплата обробляється, і ваше замовлення №' + this.code() + ' збережене. '
        + 'Ми підтвердимо його вручну - зв’яжіться з нами, якщо не отримаєте дзвінка.');
    }
  }

  private retry(why: string): string {
    return why + ' Спробуйте ще раз або оберіть інший спосіб оплати.';
  }

  /**
   * Usually the order is placed by now, so a confirmation page that fails to open must not read as a
   * failed order. Without a code it is the API sending an empty cart back to the cart page.
   */
  private async leave(redirect: string): Promise<void> {
    await this.cartService.init();
    try {
      await this.router.navigateByUrl(redirect);
    } catch {
      const code = new URLSearchParams(redirect.split('?')[1] ?? '').get('orderCode') ?? this.code();
      this.pageFailure.message.set(undefined);
      if (!code) {
        // The API sends an empty cart here, so a cart still listed is one the read above lost
        this.error.set(this.cartService.cart().products.length
          ? 'Не вдалося прочитати кошик. Спробуйте ще раз трохи згодом.'
          : 'Можливо, це замовлення вже оформлено в іншій вкладці.');
        return;
      }
      // Nothing is left to press: the order exists, and pressing again would only find an empty cart
      this.processing.set(true);
      this.error.set('Ваше замовлення №' + code + ' прийнято, але сторінку підтвердження не вдалося відкрити. '
        + 'Ми зв’яжемося з вами.');
    }
  }

}
